import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Row = {
  id: string;
  plan_no: string;
  title: string;
  theme_summary: string;
  status: string;
  session_name?: string;
  session_code?: string;
};

export default function AppPlanListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.app.manage') || user?.permissions?.includes('education.admin')
  );
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-app-plans', status, q],
    queryFn: async () => {
      const res = await api.get<Row[]>('/education/app/plans', {
        params: { status, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('appPlans')}
      description={t('appPlansIntro')}
      extra={
        canManage ? (
          <Button type="primary" onClick={() => navigate('/education/app/new')}>
            {t('newAppPlan')}
          </Button>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 160 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'draft', label: t('statusDraft') },
                  { value: 'pending_approval', label: t('statusPendingApproval') },
                  { value: 'approved', label: t('statusApproved') },
                  { value: 'closed', label: t('statusClosed') },
                  { value: 'cancelled', label: t('statusCancelled') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            onRow={(row) => ({
              onClick: () => navigate(`/education/app/${row.id}`),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: t('planNo'), dataIndex: 'plan_no', width: 150 },
              {
                title: t('session'),
                render: (_: unknown, row: Row) => row.session_name || row.session_code || '—',
              },
              { title: t('title'), dataIndex: 'title' },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 140,
                render: (v: string) => <Tag color={v === 'approved' ? 'green' : undefined}>{v}</Tag>,
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
